import { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n';
import { api } from '../../lib/api';
import { useSaveIndicator } from '../../hooks/useSaveIndicator';
import { SaveToast } from '../../components/SaveToast';

const MOCK_PAIRS = [
  { emoji: '🏛',  topic: 'Heritage' },
  { emoji: '🌿', topic: 'Sustainability' },
  { emoji: '👥', topic: 'Youth' },
  { emoji: '🤝', topic: 'Partnership' },
  { emoji: '📚', topic: 'Education' },
  { emoji: '🇵🇱', topic: 'Poland' },
  { emoji: '🇪🇺', topic: 'Europe' },
  { emoji: '🎓', topic: 'Academic' },
  { emoji: '💡', topic: 'Innovation' },
];

// Stored on the server as a JSON array of { emoji, topic } pairs.
function parsePairs(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      // Tolerate legacy { emoji: topic } object form too.
      if (parsed && typeof parsed === 'object') {
        return Object.entries(parsed)
          .filter(([emoji, topic]) => typeof emoji === 'string' && typeof topic === 'string')
          .map(([emoji, topic]) => ({ emoji, topic }));
      }
      return [];
    }
    return parsed
      .filter((p) => p && typeof p === 'object' && typeof p.emoji === 'string' && typeof p.topic === 'string')
      .map((p) => ({ emoji: p.emoji, topic: p.topic }));
  } catch {
    return [];
  }
}

export function EmojiMapTab() {
  const { t } = useTranslation();
  const { status, setSaving, flashSaved, flashError } = useSaveIndicator();

  const [pairs, setPairs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [usingMockData, setUsingMockData] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    api.brand
      .get('emoji_map')
      .then((res) => {
        if (cancelled) return;
        setPairs(parsePairs(res?.value));
        setUsingMockData(false);
      })
      .catch((err) => {
        if (cancelled) return;
        // 404 just means the key isn't seeded yet — start from an empty array
        // rather than mock fallback so the user can build up from scratch.
        if (err?.status === 404) {
          setPairs([]);
          setUsingMockData(false);
          return;
        }
        console.warn('[emoji-map] API unreachable, using mock data:', err.message);
        setPairs(MOCK_PAIRS);
        setUsingMockData(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  async function persist(next) {
    setSaving();
    try {
      await api.brand.set('emoji_map', JSON.stringify(next));
      flashSaved();
    } catch (err) {
      console.error('[emoji-map] save failed:', err);
      flashError();
    }
  }

  function remove(topic) {
    const next = pairs.filter((p) => p.topic !== topic);
    setPairs(next);
    persist(next);
  }

  if (isLoading) {
    return (
      <div className="empty-state">
        <div className="empty-state__icon" aria-hidden="true">⏳</div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="tab-page">
      {usingMockData && <div className="dev-notice">{t('events.loadError')}</div>}

      <p className="tab-page__intro">{t('brand.emojiMap.intro')}</p>

      <div className="emoji-grid">
        {pairs.map((pair) => (
          <div key={pair.topic} className="emoji-pair">
            <button
              type="button"
              className="emoji-pair__remove"
              onClick={() => remove(pair.topic)}
              aria-label={`${t('common.remove')} ${pair.topic}`}
            >
              ×
            </button>
            <span className="emoji-pair__emoji" aria-hidden="true">{pair.emoji}</span>
            <span className="emoji-pair__topic">{pair.topic}</span>
          </div>
        ))}
        {/* TODO: wire add-mapping form (emoji picker + topic input). For now
            this is a visual placeholder; once added it'll call persist(). */}
        <button type="button" className="emoji-pair emoji-pair--add" disabled>
          + {t('brand.common.addMapping')}
        </button>
      </div>

      <SaveToast status={status} />
    </div>
  );
}
